import type { DeviceCodeBlockingPolicy, MintReadiness } from '../api/waves';

// FD-592 (FD-ADR-047 §2) — what the named device-code probe actually said.
//
// `account.deviceCodeBlockedBy` carries FOUR meanings, and the Activate flow must
// keep them apart, because each one tells the operator something different:
//
//   `undefined`  — the Manager predates FD-591 and never asked. Say NOTHING: a
//                  silence about a question nobody asked is not "unknown".
//   `null`       — the probe ran and could not decide. Say "could not check".
//   `[]`         — checked, and no device-code-blocking policy covers the account.
//   `[…]`        — these named policies block (`covers`) or may block (`may-cover`).
//
// Report-only policies (`enabledForReportingButNotEnforced`) LOG a device-code
// sign-in; they do not block one. They are surfaced as such and never counted as a
// block, because steering an operator away from a path that works is the exact
// false block FD-ADR-047 §2's check exists to replace with a named fact.
//
// ADVISORY, like everything else on the Activate flow: this decides copy and which
// path to suggest. It never disables a path — `activate-wave` refuses on its own
// terms, and a refused redeem names `AADSTS530036` (FD-588).

export type DeviceCodeBlockState =
  | { kind: 'not-asked' }
  | { kind: 'unknown' }
  | { kind: 'clear' }
  | {
      kind: 'policies';
      covers: DeviceCodeBlockingPolicy[];
      mayCover: DeviceCodeBlockingPolicy[];
      reportOnly: DeviceCodeBlockingPolicy[];
    };

function isPolicy(value: unknown): value is DeviceCodeBlockingPolicy {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { displayName?: unknown }).displayName === 'string'
  );
}

export function deviceCodeBlockState(
  readiness: MintReadiness | null | undefined,
): DeviceCodeBlockState {
  // No readiness answer at all is the same as "not asked" for this question: the
  // panel above already says the readiness read failed.
  const account = readiness?.account;
  if (!account || !('deviceCodeBlockedBy' in account) || account.deviceCodeBlockedBy === undefined) {
    return { kind: 'not-asked' };
  }
  const raw = account.deviceCodeBlockedBy as unknown;
  if (raw === null) return { kind: 'unknown' };

  // A one-element array can arrive as a bare object from a PowerShell producer
  // (ConvertTo-Json unwraps it); accept both rather than misreport a block as clear.
  const list = (Array.isArray(raw) ? raw : [raw]).filter(isPolicy);
  if (list.length === 0) return { kind: 'clear' };

  const reportOnly = list.filter((p) => p.state === 'enabledForReportingButNotEnforced');
  const enforced = list.filter((p) => p.state !== 'enabledForReportingButNotEnforced');
  const covers = enforced.filter((p) => p.certainty === 'covers');
  const mayCover = enforced.filter((p) => p.certainty !== 'covers');
  return { kind: 'policies', covers, mayCover, reportOnly };
}

/** True only when a policy is known or suspected to BLOCK device code. Report-only never counts. */
export function deviceCodeLikelyBlocked(state: DeviceCodeBlockState): boolean {
  return state.kind === 'policies' && (state.covers.length > 0 || state.mayCover.length > 0);
}
